import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import ClientCombobox from "../components/ClientCombobox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import { FilePlus, Warning, CheckCircle, PencilSimple, Trash, FilePdf, X, Plus as PlusIcon, ChartBar, MagnifyingGlass, SquaresFour, Rows, DownloadSimple, UploadSimple } from "@phosphor-icons/react";
import { API_BASE } from "../lib/api";

// Catalogue standard des services managés — libellés affichés à l'utilisateur.
// Les valeurs stockées côté backend sont les libellés eux-mêmes (chaînes libres),
// ce qui permet aux administrateurs d'ajouter facilement des services personnalisés.
const SERVICE_CATALOG = [
  "Télésauvegarde",
  "Antispam",
  "Antivirus",
  "Office 365",
  "Microsoft 365 Business",
  "Supervision serveur",
  "Firewall managé",
  "Hébergement web",
  "Nom de domaine",
  "Certificat SSL",
  "Téléphonie IP",
  "Maintenance préventive",
  "Formation utilisateurs",
  "Sauvegarde cloud",
];

export default function Contracts() {
  const [contracts, setContracts] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ client_id: "", name: "", annual_hours: 120, monthly_package: 800, overrun_rate: 90, start_date: "", end_date: "", notes: "", services: [] });
  const [customService, setCustomService] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem("contracts_view_mode") || "cards"; } catch { return "cards"; }
  });
  const changeViewMode = (m) => {
    setViewMode(m);
    try { localStorage.setItem("contracts_view_mode", m); } catch { /* noop */ }
  };
  const [importReport, setImportReport] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const importInputRef = useRef(null);

  const exportCsv = async () => {
    try {
      const r = await api.get("/contracts/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `contrats_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${contracts.length} contrat${contracts.length > 1 ? "s" : ""} exporté${contracts.length > 1 ? "s" : ""}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Export impossible");
    }
  };

  const importCsv = async (file) => {
    if (!file) return;
    setImportBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/contracts/import.csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportReport(r.data);
      toast.success(`${r.data.created} créé${r.data.created > 1 ? "s" : ""} · ${r.data.updated} mis à jour · ${r.data.skipped} ignoré${r.data.skipped > 1 ? "s" : ""}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import impossible");
    } finally {
      setImportBusy(false);
      if (importInputRef.current) importInputRef.current.value = "";
    }
  };

  const nav = useNavigate();
  const load = async () => {
    try {
      const [c, u] = await Promise.all([
        api.get("/contracts"),
        api.get("/users", { params: { role: "client" } }),
      ]);
      setContracts(c.data);
      setClients(u.data);
    } catch (err) {
      // v284 — Contrats est un Module : quand il est désactivé pour les techs,
      // le backend renvoie 403. On redirige silencieusement vers /modules.
      if (err?.response?.status === 403) {
        toast.info("Module Contrats désactivé — demandez à un administrateur de l'activer.");
        nav("/modules");
        return;
      }
      throw err;
    }
  };
  const [companies, setCompanies] = useState([]);
  useEffect(() => { load(); (async () => { try { const r = await api.get("/companies"); setCompanies(r.data); } catch { /* ignore */ } })(); }, []);

  // Auto-open "new contract" dialog with a preselected client — /contrats?new=1&client_id=xxx
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      const cid = searchParams.get("client_id") || "";
      setEditing(null);
      setForm({ client_id: cid, name: "", monthly_hours: 10, hourly_rate: 90, start_date: "", end_date: "", notes: "" });
      setOpen(true);
      // Clean the URL so a page refresh doesn't reopen the dialog
      const next = new URLSearchParams(searchParams);
      next.delete("new"); next.delete("client_id");
      setSearchParams(next, { replace: true });
    }
  }, []);

  const openNew = () => { setEditing(null); setForm({ client_id: "", company_id: "", name: "", annual_hours: 120, monthly_package: 800, overrun_rate: 90, start_date: "", end_date: "", notes: "", services: [] }); setCustomService(""); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      client_id: c.client_id,
      company_id: c.company_id || "",
      name: c.name,
      annual_hours: c.annual_hours || (c.monthly_hours ? c.monthly_hours * 12 : 120),
      monthly_package: c.monthly_package || (c.hourly_rate && c.monthly_hours ? c.hourly_rate * c.monthly_hours : 800),
      overrun_rate: c.overrun_rate || c.hourly_rate || 90,
      start_date: c.start_date || "",
      end_date: c.end_date || "",
      notes: c.notes || "",
      services: c.services || [],
    });
    setCustomService("");
    setOpen(true);
  };

  const toggleService = (svc) => {
    setForm((f) => ({
      ...f,
      services: (f.services || []).includes(svc)
        ? f.services.filter((s) => s !== svc)
        : [...(f.services || []), svc],
    }));
  };
  const addCustomService = () => {
    const v = customService.trim();
    if (!v) return;
    if ((form.services || []).includes(v)) { setCustomService(""); return; }
    setForm({ ...form, services: [...(form.services || []), v] });
    setCustomService("");
  };
  const removeService = (svc) => setForm({ ...form, services: (form.services || []).filter((s) => s !== svc) });

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.patch(`/contracts/${editing.id}`, form);
        toast.success("Contrat mis à jour");
      } else {
        await api.post("/contracts", form);
        toast.success("Contrat créé");
      }
      setOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Supprimer définitivement le contrat "${c.name}" ?\n\nLes tickets historiques rattachés à ce contrat ne seront PAS supprimés — ils restent visibles mais sans contrat lié.\n\nCette action est irréversible.`)) return;
    try {
      await api.delete(`/contracts/${c.id}`);
      toast.success("Contrat supprimé");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Suppression impossible");
    }
  };

  const downloadPdf = async (c) => {
    try {
      const r = await api.get(`/contracts/${c.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de générer le PDF");
    }
  };

  const downloadMonthly = async (c) => {
    const now = new Date();
    const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    const month = window.prompt("Mois du rapport (format YYYY-MM)", defaultMonth) || defaultMonth;
    if (!/^\d{4}-\d{2}$/.test(month)) { toast.error("Format invalide"); return; }
    try {
      const r = await api.get(`/clients/${c.client_id}/monthly-report/pdf`, { params: { month }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(e.response?.data?.detail || "Impossible de générer le rapport"); }
  };

  const alertColor = (a) => a === "critical" ? "bg-red-500" : a === "warning" ? "bg-orange-500" : "bg-emerald-500";
  const alertBg = (a) => a === "critical" ? "border-l-red-500" : a === "warning" ? "border-l-orange-500" : "border-l-emerald-500";

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return contracts;
    return contracts.filter((c) => {
      if ((c.name || "").toLowerCase().includes(s)) return true;
      if ((c.client_name || "").toLowerCase().includes(s)) return true;
      if ((c.client_email || "").toLowerCase().includes(s)) return true;
      if ((c.client_company || "").toLowerCase().includes(s)) return true;
      if ((c.client_phone || "").toLowerCase().includes(s)) return true;
      if ((c.notes || "").toLowerCase().includes(s)) return true;
      if ((c.services || []).some((sv) => (sv || "").toLowerCase().includes(s))) return true;
      return false;
    });
  }, [contracts, search]);

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-caps">Facturation</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold mt-1">Contrats de maintenance</h1>
          <p className="text-sm text-muted-foreground mt-1">Heures mensuelles allouées et consommées par client. Alertes automatiques à partir de 80 %.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="secondary" size="sm" onClick={exportCsv} data-testid="contracts-export-csv">
            <DownloadSimple size={14} className="mr-1.5"/> Export CSV
          </Button>
          <Button variant="secondary" size="sm" onClick={() => importInputRef.current?.click()}
            disabled={importBusy} data-testid="contracts-import-csv">
            <UploadSimple size={14} className="mr-1.5"/>{importBusy ? "Import…" : "Import CSV"}
          </Button>
          <input ref={importInputRef} type="file" accept=".csv,.txt,text/csv" className="hidden"
            data-testid="contracts-import-input"
            onChange={(e) => importCsv(e.target.files?.[0])}/>
          <Button data-testid="new-contract-btn" onClick={openNew}><FilePlus size={16} className="mr-2"/> Nouveau contrat</Button>
        </div>
      </header>

      <Card>
        <CardContent className="pt-6 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
            <Input
              data-testid="contract-search"
              className="pl-9"
              placeholder="Rechercher par nom de contrat, client, société, email, téléphone, service, notes…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-1 rounded-md border p-1 bg-muted/40" role="group" aria-label="Mode d'affichage">
            <Button
              size="sm"
              variant={viewMode === "cards" ? "default" : "ghost"}
              onClick={() => changeViewMode("cards")}
              data-testid="contract-view-cards"
              title="Vue cartes"
              className="h-7 px-2.5"
            >
              <SquaresFour size={14} className="mr-1.5"/> Cartes
            </Button>
            <Button
              size="sm"
              variant={viewMode === "list" ? "default" : "ghost"}
              onClick={() => changeViewMode("list")}
              data-testid="contract-view-list"
              title="Vue liste"
              className="h-7 px-2.5"
            >
              <Rows size={14} className="mr-1.5"/> Liste
            </Button>
          </div>
          <span className="text-xs text-muted-foreground whitespace-nowrap" data-testid="contract-count">
            {filtered.length}{search ? ` / ${contracts.length}` : ""} contrat{filtered.length > 1 ? "s" : ""}
          </span>
        </CardContent>
      </Card>

      {viewMode === "list" && (
        <Card><CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm table-auto min-w-[1100px]" data-testid="contracts-table">
              <thead className="text-xs uppercase text-muted-foreground border-b">
                <tr>
                  <th className="text-left py-3 px-4 w-8"></th>
                  <th className="text-left py-3 px-4">Contrat</th>
                  <th className="text-left py-3 px-4">Client / Société</th>
                  <th className="text-right py-3 px-4 whitespace-nowrap">Consommé</th>
                  <th className="text-left py-3 px-4 w-[180px]">Progression</th>
                  <th className="text-right py-3 px-4 whitespace-nowrap">Tarif</th>
                  <th className="text-right py-3 px-4 whitespace-nowrap">Fin</th>
                  <th className="text-right py-3 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && contracts.length > 0 && (
                  <tr><td colSpan={8} className="text-center py-10 text-muted-foreground">Aucun résultat pour « {search} ».</td></tr>
                )}
                {contracts.length === 0 && (
                  <tr><td colSpan={8} className="text-center py-10 text-muted-foreground">Aucun contrat. Créez-en un pour suivre les heures par client.</td></tr>
                )}
                {filtered.map((c) => (
                  <tr key={c.id} className="border-b hover:bg-muted/40 align-middle" data-testid={`contract-row-${c.id}`}>
                    <td className="py-3 px-4">
                      <span className={`inline-block w-2.5 h-2.5 rounded-full ${alertColor(c.alert)}`} title={c.alert || "ok"}/>
                    </td>
                    <td className="py-3 px-4">
                      <div className="font-medium">{c.name}</div>
                      {(c.services && c.services.length > 0) && (
                        <div className="text-[0.7rem] text-muted-foreground truncate max-w-[280px]" title={c.services.join(" · ")}>
                          {c.services.slice(0, 3).join(" · ")}{c.services.length > 3 ? ` · +${c.services.length - 3}` : ""}
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-sm">{c.client_name}</div>
                      <div className="text-xs text-muted-foreground truncate max-w-[260px]">
                        {c.client_company ? c.client_company : <span className="italic">—</span>}
                        {c.client_email ? <> · <span className="mono">{c.client_email}</span></> : null}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right mono text-sm whitespace-nowrap">
                      <span className={c.over_limit ? "text-destructive font-semibold" : ""}>
                        {c.annual_hours_consumed}h / {c.annual_hours}h
                      </span>
                      {c.over_limit && <div className="text-[0.65rem] text-destructive">+{c.annual_overrun_hours}h HF</div>}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Progress value={Math.min(100, c.annual_percent)} className="h-1.5 flex-1"/>
                        <span className={`text-xs mono ${c.over_limit ? "text-destructive font-semibold" : "text-muted-foreground"}`}>
                          {c.annual_percent}%
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right mono text-sm whitespace-nowrap">
                      {(c.monthly_package || 0).toFixed(0)} €/mois
                      <div className="text-[0.65rem] text-muted-foreground">+{c.overrun_rate}€/h HF</div>
                    </td>
                    <td className="py-3 px-4 text-right text-xs whitespace-nowrap">{c.end_date || <span className="text-muted-foreground">—</span>}</td>
                    <td className="py-3 px-4 text-right whitespace-nowrap">
                      <Button size="icon" variant="ghost" className="h-8 w-8" title="PDF"
                        onClick={() => downloadPdf(c)} data-testid={`pdf-contract-row-${c.id}`}>
                        <FilePdf size={14}/>
                      </Button>
                      <Link to={`/contrats/${c.id}/stats`} data-testid={`stats-contract-row-${c.id}`} className="inline-block">
                        <Button size="icon" variant="ghost" className="h-8 w-8" title="Stats">
                          <ChartBar size={14}/>
                        </Button>
                      </Link>
                      <Button size="icon" variant="ghost" className="h-8 w-8" title="Modifier"
                        onClick={() => openEdit(c)} data-testid={`edit-contract-row-${c.id}`}>
                        <PencilSimple size={14}/>
                      </Button>
                      <Button size="icon" variant="ghost"
                        className="h-8 w-8 text-destructive hover:text-destructive hover:bg-red-50 dark:hover:bg-red-950/30"
                        title="Supprimer"
                        onClick={() => remove(c)} data-testid={`del-contract-row-${c.id}`}>
                        <Trash size={14}/>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent></Card>
      )}

      {viewMode === "cards" && (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((c) => (
          <Card key={c.id} data-testid={`contract-${c.id}`} className={`border-l-4 ${alertBg(c.alert)} card-hover`}>
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <div className="label-caps">{c.client_name}</div>
                  <h3 className="font-display text-xl font-semibold leading-snug">{c.name}</h3>
                </div>
                <div className={`w-2.5 h-2.5 rounded-full ${alertColor(c.alert)} mt-2`}/>
              </div>

              <div className="grid grid-cols-2 gap-2 mt-3">
                <div>
                  <div className="text-[0.65rem] uppercase tracking-wider text-muted-foreground">Contact</div>
                  <div className="font-medium truncate">{c.client_name}</div>
                  <div className="text-xs text-muted-foreground truncate">{c.client_email || <span className="italic">Non renseigné</span>}</div>
                  <div className="text-xs text-muted-foreground">{c.client_phone || <span className="italic">Tél. non renseigné</span>}</div>
                </div>
                <div>
                  <div className="text-[0.65rem] uppercase tracking-wider text-muted-foreground">Société</div>
                  <div className="font-medium">{c.client_company || <span className="italic text-muted-foreground">—</span>}</div>
                  <div className="text-xs text-muted-foreground truncate">{c.client_address || <span className="italic">Adresse non renseignée</span>}</div>
                </div>
              </div>

              {(c.services && c.services.length > 0) && (
                <div className="mt-4" data-testid={`contract-services-${c.id}`}>
                  <div className="text-[0.65rem] uppercase tracking-wider text-muted-foreground mb-1.5">Services souscrits</div>
                  <div className="flex flex-wrap gap-1.5">
                    {c.services.map((s) => (
                      <span key={s}
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-[0.7rem] font-medium bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-5 space-y-2">
                {c.over_limit && (
                  <div className="rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 px-3 py-2 flex items-center gap-2 text-xs font-semibold text-red-700 dark:text-red-300">
                    <Warning size={14} weight="fill"/> Dépassement forfait annuel : +{c.annual_overrun_hours}h ({c.annual_overrun_cost?.toFixed?.(2) || 0}€ HT)
                  </div>
                )}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Consommé cette année</span>
                  <span className="mono font-semibold">{c.annual_hours_consumed}h / {c.annual_hours}h</span>
                </div>
                <Progress value={Math.min(100, c.annual_percent)} className="h-2"/>
                <div className="flex items-center justify-between text-xs">
                  <span className={c.over_limit ? "text-destructive font-semibold flex items-center gap-1" : c.annual_percent >= 80 ? "text-orange-600 flex items-center gap-1" : "text-emerald-600 flex items-center gap-1"}>
                    {c.over_limit ? <><Warning size={12}/> Hors forfait</> : c.annual_percent >= 80 ? <><Warning size={12}/> Proche du quota</> : <><CheckCircle size={12}/> Sous quota</>}
                  </span>
                  <span className="mono text-muted-foreground">{c.annual_percent}% du forfait annuel</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 mt-5 text-sm">
                <div>
                  <div className="text-[0.65rem] uppercase tracking-wider text-muted-foreground">Forfait mensuel</div>
                  <div className="font-medium mono">{(c.monthly_package || 0).toFixed(2)} €/mois</div>
                </div>
                <div>
                  <div className="text-[0.65rem] uppercase tracking-wider text-muted-foreground">Tarif dépassement</div>
                  <div className="font-medium mono">{c.overrun_rate} €/h</div>
                </div>
                <div>
                  <div className="text-[0.65rem] uppercase tracking-wider text-muted-foreground">Consommé ce mois</div>
                  <div className="font-medium mono">{c.hours_consumed}h / {c.monthly_quota}h</div>
                </div>
                <div>
                  <div className="text-[0.65rem] uppercase tracking-wider text-muted-foreground">Fin contrat</div>
                  <div className="font-medium">{c.end_date || "—"}</div>
                </div>
              </div>

              <div className="flex gap-2 mt-4 pt-4 border-t">
                <Button size="sm" variant="secondary" onClick={() => downloadPdf(c)} data-testid={`pdf-contract-${c.id}`}>
                  <FilePdf size={14} className="mr-1.5"/> PDF
                </Button>
                <Link to={`/contrats/${c.id}/stats`} data-testid={`stats-contract-${c.id}`}>
                  <Button size="sm" variant="secondary">
                    <ChartBar size={14} className="mr-1.5"/> Stats
                  </Button>
                </Link>
                <Button size="sm" variant="secondary" onClick={() => downloadMonthly(c)} data-testid={`pdf-monthly-${c.id}`}>
                  Rapport mois
                </Button>
                <div className="flex-1"/>
                <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`edit-contract-${c.id}`} title="Modifier le contrat"><PencilSimple size={14}/></Button>
                <Button size="sm" variant="ghost"
                  className="text-destructive hover:text-destructive hover:bg-red-50 dark:hover:bg-red-950/30"
                  onClick={() => remove(c)}
                  data-testid={`del-contract-${c.id}`}
                  title="Supprimer le contrat">
                  <Trash size={14}/>
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {filtered.length === 0 && contracts.length > 0 && (
          <p className="text-muted-foreground col-span-full text-center py-10">Aucun résultat pour « {search} ».</p>
        )}
        {contracts.length === 0 && (
          <p className="text-muted-foreground col-span-full text-center py-10">Aucun contrat. Créez-en un pour suivre les heures par client.</p>
        )}
      </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Modifier le contrat" : "Nouveau contrat"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            {!editing && (
              <div>
                <Label>Client (contact principal)</Label>
                <ClientCombobox
                  clients={clients}
                  value={form.client_id}
                  onChange={(v) => setForm({...form, client_id: v})}
                  allowNew={false}
                />
              </div>
            )}
            <div>
              <Label>Société rattachée <span className="text-xs text-muted-foreground font-normal">(pour lier tous les contacts du même domaine email)</span></Label>
              <select
                data-testid="contract-company"
                className="w-full h-9 rounded-md border bg-background px-3 text-sm"
                value={form.company_id || ""}
                onChange={(e) => setForm({...form, company_id: e.target.value || null})}
              >
                <option value="">— Aucune société —</option>
                {companies.map((co) => {
                  const hasContract = (co.contracts_count || 0) > 0;
                  const isCurrent = editing && editing.company_id === co.id;
                  return (
                    <option key={co.id} value={co.id} disabled={hasContract && !isCurrent}>
                      {co.name} ({(co.email_domains || []).join(", ") || "—"}){hasContract && !isCurrent ? " · déjà contractualisée" : ""}
                    </option>
                  );
                })}
              </select>
              <p className="text-xs text-muted-foreground mt-1">Une société ne peut avoir qu&apos;un seul contrat actif. Les sociétés déjà pourvues sont grisées.</p>
            </div>
            <div>
              <Label>Nom du contrat</Label>
              <Input required data-testid="contract-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Ex. Forfait mensuel Standard"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Heures/an (forfait annuel)</Label>
                <Input type="number" min={0} step={1} required data-testid="contract-annual-hours"
                  value={form.annual_hours} onChange={(e) => setForm({...form, annual_hours: parseFloat(e.target.value)})}/>
              </div>
              <div>
                <Label>Forfait mensuel (€/mois)</Label>
                <Input type="number" min={0} step={10} required data-testid="contract-monthly-package"
                  value={form.monthly_package} onChange={(e) => setForm({...form, monthly_package: parseFloat(e.target.value)})}/>
              </div>
              <div className="col-span-2">
                <Label>Tarif dépassement (€/h hors forfait)</Label>
                <Input type="number" min={0} step={1} required data-testid="contract-overrun-rate"
                  value={form.overrun_rate} onChange={(e) => setForm({...form, overrun_rate: parseFloat(e.target.value)})}/>
                <p className="text-xs text-muted-foreground mt-1">Appliqué uniquement aux heures dépassant le forfait annuel.</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Début</Label>
                <Input type="date" data-testid="contract-start" value={form.start_date} onChange={(e) => setForm({...form, start_date: e.target.value})}/>
              </div>
              <div>
                <Label>Fin</Label>
                <Input type="date" data-testid="contract-end" value={form.end_date} onChange={(e) => setForm({...form, end_date: e.target.value})}/>
              </div>
            </div>
            <div data-testid="contract-services-editor">
              <Label>Services souscrits</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Cochez tous les services managés dont ce client bénéficie chez vous.
              </p>
              <div className="grid grid-cols-2 gap-1.5 max-h-52 overflow-y-auto pr-1">
                {SERVICE_CATALOG.map((svc) => {
                  const checked = (form.services || []).includes(svc);
                  return (
                    <label key={svc}
                      className={`flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-sm cursor-pointer transition ${checked ? "bg-blue-50 border-blue-300 dark:bg-blue-950/40 dark:border-blue-700" : "hover:bg-muted"}`}>
                      <input type="checkbox" checked={checked} onChange={() => toggleService(svc)}
                        className="accent-primary" data-testid={`service-cb-${svc}`}/>
                      <span className="truncate">{svc}</span>
                    </label>
                  );
                })}
              </div>

              {/* Custom services (added on the fly) */}
              {(form.services || []).some((s) => !SERVICE_CATALOG.includes(s)) && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {form.services.filter((s) => !SERVICE_CATALOG.includes(s)).map((s) => (
                    <span key={s} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800">
                      {s}
                      <button type="button" onClick={() => removeService(s)} className="hover:text-emerald-900 dark:hover:text-white">
                        <X size={11}/>
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <div className="mt-2 flex gap-2">
                <Input
                  data-testid="contract-service-custom"
                  placeholder="Ajouter un service personnalisé…"
                  value={customService}
                  onChange={(e) => setCustomService(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomService(); } }}
                />
                <Button type="button" variant="secondary" onClick={addCustomService} data-testid="contract-service-add">
                  <PlusIcon size={14} className="mr-1"/> Ajouter
                </Button>
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea data-testid="contract-notes" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})}/>
            </div>
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Annuler</Button>
              <Button type="submit" data-testid="contract-save">{editing ? "Enregistrer" : "Créer"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import CSV report dialog */}
      <Dialog open={!!importReport} onOpenChange={(v) => { if (!v) setImportReport(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">Rapport d&apos;import CSV — Contrats</DialogTitle>
          </DialogHeader>
          {importReport && (
            <div className="space-y-4" data-testid="contracts-import-report">
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg border p-3 text-center bg-emerald-50 dark:bg-emerald-950/30">
                  <div className="text-2xl font-bold text-emerald-600">{importReport.created}</div>
                  <div className="text-xs uppercase text-muted-foreground">créés</div>
                </div>
                <div className="rounded-lg border p-3 text-center bg-blue-50 dark:bg-blue-950/30">
                  <div className="text-2xl font-bold text-blue-600">{importReport.updated}</div>
                  <div className="text-xs uppercase text-muted-foreground">mis à jour</div>
                </div>
                <div className="rounded-lg border p-3 text-center bg-amber-50 dark:bg-amber-950/30">
                  <div className="text-2xl font-bold text-amber-600">{importReport.skipped}</div>
                  <div className="text-xs uppercase text-muted-foreground">ignorés</div>
                </div>
              </div>
              {importReport.errors && importReport.errors.length > 0 && (
                <div className="rounded-lg border p-3 bg-red-50 dark:bg-red-950/20">
                  <div className="text-sm font-semibold text-destructive mb-2">
                    {importReport.errors.length} erreur{importReport.errors.length > 1 ? "s" : ""} :
                  </div>
                  <ul className="text-xs space-y-1 max-h-64 overflow-y-auto">
                    {importReport.errors.map((e, i) => (
                      <li key={i} className="border-l-2 border-red-400 pl-2">
                        <span className="font-mono text-muted-foreground">L.{e.line}</span> · <span className="font-medium">{e.name || "(sans nom)"}</span> — {e.error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Colonnes acceptées : <span className="mono">name</span> (obl.) · <span className="mono">client_email</span> (obl.) · company_name · monthly_hours · hourly_rate · start_date · end_date · active · services (séparés par <span className="mono">|</span>) · notes.
                Le matching se fait sur (name + client_email) : existe → update, sinon → create.
              </p>
              <DialogFooter>
                <Button onClick={() => setImportReport(null)} data-testid="contracts-import-report-close">Fermer</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
